/**
 * OBD101 fitment — classic CAN (11/29-bit) on supported GM / Ford / Stellantis brands.
 * Returns fit status for garage vehicles. CAN-FD-only OBD paths are treated as incompatible.
 */
(function (global) {
  'use strict';

  var MIN_YEAR = 1996;

  var MAKE_GROUPS = {
    gm: [
      'chevrolet', 'chevy', 'gmc', 'buick', 'cadillac', 'pontiac', 'saturn', 'hummer', 'oldsmobile'
    ],
    ford: ['ford', 'lincoln', 'mercury'],
    stellantis: ['chrysler', 'dodge', 'jeep', 'ram', 'plymouth']
  };

  /**
   * Models that need CAN FD on the OBD path (classic CAN not sufficient).
   * Sources: GM VIP/Global B, Ford CAN-FD rollout, Stellantis STLA (industry scan-tool lists).
   * Each rule: { group, fromYear, models: string[] | null (null = every model in group) }
   */
  var CAN_FD_OBD_RULES = [
    { group: 'gm', fromYear: 2020, models: ['corvette', 'ct4', 'ct5'] },
    {
      group: 'gm',
      fromYear: 2021,
      models: [
        'tahoe', 'suburban', 'yukon', 'escalade', 'silverado', 'sierra', 'avalanche',
        'blazer', 'traverse', 'enclave', 'acadia', 'xt4', 'xt5', 'xt6', 'bolt', 'hummer ev', 'hummer'
      ]
    },
    { group: 'ford', fromYear: 2018, models: ['ecosport', 'expedition', 'navigator'] },
    { group: 'ford', fromYear: 2019, models: ['ranger', 'transit connect'] },
    {
      group: 'ford',
      fromYear: 2020,
      models: [
        'escape', 'corsair', 'explorer', 'aviator', 'super duty', 'f-250', 'f-350', 'f-450', 'f-550',
        'transit', 'mustang', 'mach-e', 'mach e', 'bronco sport', 'bronco'
      ]
    },
    { group: 'stellantis', fromYear: 2024, models: null },
    { group: 'stellantis', fromYear: 2022, models: ['wagoneer', 'grand wagoneer'] }
  ];

  function norm(text) {
    return (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/^make:/, '')
      .replace(/[^\w\s.-]/g, ' ')
      .replace(/\s+/g, ' ');
  }

  function normModel(text) {
    return norm(text).replace(/\s+/g, ' ');
  }

  function getMakeGroup(make) {
    var m = norm(make);
    var key;
    for (key in MAKE_GROUPS) {
      if (MAKE_GROUPS[key].indexOf(m) !== -1) return key;
    }
    return null;
  }

  function modelMatches(model, patterns) {
    var vm = normModel(model);
    if (!patterns || !patterns.length) return true;
    for (var i = 0; i < patterns.length; i++) {
      var p = normModel(patterns[i]);
      if (!p) continue;
      if (vm === p || vm.indexOf(p) !== -1 || p.indexOf(vm) !== -1) return true;
      if (vm.replace(/[\s-]/g, '') === p.replace(/[\s-]/g, '')) return true;
    }
    return false;
  }

  function isCanFdObdVehicle(year, make, model) {
    var y = parseInt(year, 10);
    if (!y) return false;
    var group = getMakeGroup(make);
    if (!group) return false;

    for (var i = 0; i < CAN_FD_OBD_RULES.length; i++) {
      var rule = CAN_FD_OBD_RULES[i];
      if (rule.group !== group) continue;
      if (y < rule.fromYear) continue;
      if (rule.models === null) return true;
      if (modelMatches(model, rule.models)) return true;
    }
    return false;
  }

  function check(vehicle) {
    if (!vehicle || !vehicle.year || !vehicle.make || !vehicle.model) {
      return {
        status: 'unknown',
        compatible: false,
        title: 'Select a vehicle in My Garage',
        detail: 'Add your year, make, and model to check OBD101 compatibility.'
      };
    }

    var year = parseInt(vehicle.year, 10);
    var make = vehicle.make;
    var model = vehicle.baseModel || vehicle.model;
    var group = getMakeGroup(make);

    if (year < MIN_YEAR) {
      return {
        status: 'incompatible',
        compatible: false,
        code: 'pre_obd2',
        title: 'Does not fit your ' + vehicle.year + ' ' + make + ' ' + model,
        detail: 'OBD101 requires a 1996 or newer vehicle.'
      };
    }

    if (!group) {
      return {
        status: 'incompatible',
        compatible: false,
        code: 'unsupported_make',
        title: 'Not supported for your ' + vehicle.year + ' ' + make + ' ' + model,
        detail: 'OBD101 is designed for GM, Ford, and Chrysler vehicles.'
      };
    }

    if (isCanFdObdVehicle(year, make, model)) {
      return {
        status: 'incompatible',
        compatible: false,
        code: 'can_fd',
        title: 'Does not fit your ' + vehicle.year + ' ' + make + ' ' + model,
        detail: 'This vehicle is not currently supported by OBD101.'
      };
    }

    return {
      status: 'compatible',
      compatible: true,
      code: 'classic_can',
      title: 'Works with your ' + vehicle.year + ' ' + make + ' ' + model,
      detail: ''
    };
  }

  /** Human-readable exclusion list for docs / support */
  function getCanFdExclusionSummary() {
    return CAN_FD_OBD_RULES.slice();
  }

  global.OBD101Fitment = {
    check: check,
    getMakeGroup: getMakeGroup,
    isCanFdObdVehicle: isCanFdObdVehicle,
    getCanFdExclusionSummary: getCanFdExclusionSummary,
    MAKE_GROUPS: MAKE_GROUPS,
    CAN_FD_OBD_RULES: CAN_FD_OBD_RULES
  };
})(window);
